import { ComponentProps, ForwardedRef, forwardRef, useState } from 'react';

import { Collapse } from 'react-bootstrap';
import TableCell from '@/common/Table/TableCell';
import { getClasses } from '@/utils';
import useIsVisible from '@/hooks/useIsVisible';

export type TableRowProps = ComponentProps<'tr'> & { header?: boolean; virtualize?: boolean };

const TableRow = forwardRef(
  (
    { className, children, header, virtualize = true, ...props }: TableRowProps,
    forwardedRef: ForwardedRef<HTMLTableRowElement>
  ): JSX.Element => {
    const [ref, isVisible] = useIsVisible<HTMLTableRowElement>([], {
      threshold: 0.25,
    });
    const isHeader = header !== undefined && header !== false;
    const classes = getClasses('TableRow', isHeader ? 'TableRow-Header' : undefined, className);

    const setRefs = (node: HTMLTableRowElement | null): void => {
      ref.current = node;
      if (typeof forwardedRef === 'function') forwardedRef(node);
      else if (forwardedRef) forwardedRef.current = node;
    };

    return (
      <tr {...props} className={classes} ref={setRefs}>
        {isHeader || isVisible || virtualize === false ? children : <TableCell />}
      </tr>
    );
  }
);
TableRow.displayName = 'TableRow';

export type TableCollapseProps = Omit<TableRowProps, 'content'> & {
  content: JSX.Element;
  in?: boolean;
};

export const TableCollapse = ({ content, in: inProp, onClick, ...props }: TableCollapseProps): JSX.Element => {
  const [expanded, setExpanded] = useState(false);
  const handleClick = (event: React.MouseEvent<HTMLTableRowElement, MouseEvent>): void => {
    setExpanded(!expanded);
    onClick?.(event);
  };

  if (!content) return <TableRow {...props} />;
  return (
    <>
      <TableRow {...props} onClick={handleClick} />
      <tr className="TableCollapse">
        <td colSpan={0}>
          <Collapse in={inProp ?? expanded}>
            <div className="TableCollapse-Content">{content}</div>
          </Collapse>
        </td>
      </tr>
    </>
  );
};

export default TableRow;
